import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Save, Loader2 } from 'lucide-react'
import type { Case, ClientProfile, CasePriority } from '../../types/case-management'
import type { EditCaseFormState } from './EditCaseModal'

const priorityColors: Record<CasePriority, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-500',
  low: 'bg-slate-400',
}

interface ClientDropdownPanelProps {
  clients: ClientProfile[]
  selectedClientId: string | null
  clientSearch: string
  onSelect: (id: string | null) => void
  onSearchChange: (value: string) => void
}

function ClientDropdownPanel({
  clients,
  selectedClientId,
  clientSearch,
  onSelect,
  onSearchChange,
}: ClientDropdownPanelProps): ReactElement {
  const { t } = useTranslation(['cases'])
  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.shortName?.toLowerCase().includes(clientSearch.toLowerCase())
  )
  return (
    <div className="absolute z-10 mt-1 max-h-80 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
      <div className="border-b border-slate-200 p-2 dark:border-slate-700">
        <input
          type="text"
          value={clientSearch}
          onChange={(e) => {
            onSearchChange(e.target.value)
          }}
          placeholder={t('newCase.searchClients')}
          className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:ring-1 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        />
      </div>
      <div className="max-h-48 overflow-y-auto">
        <button
          type="button"
          onClick={() => {
            onSelect(null)
          }}
          className="w-full px-4 py-2.5 text-left text-slate-500 italic transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
        >
          {t('editCase.clearAssignment')}
        </button>
        {filteredClients.map((client) => (
          <button
            key={client.id}
            type="button"
            onClick={() => {
              onSelect(client.id)
            }}
            className={`w-full px-4 py-2.5 text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-700 ${selectedClientId === client.id ? 'bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10' : ''}`}
          >
            <span className="text-slate-900 dark:text-slate-100">{client.name}</span>
            {client.shortName && (
              <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                ({client.shortName})
              </span>
            )}
            {client.industry && (
              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                {client.industry}
              </span>
            )}
          </button>
        ))}
        {filteredClients.length === 0 && clientSearch && (
          <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            {t('newCase.noClientsFound')}
          </div>
        )}
      </div>
    </div>
  )
}

interface EditCaseClientDropdownProps {
  clients: ClientProfile[]
  selectedClientId: string | null
  showDropdown: boolean
  clientSearch: string
  onToggle: () => void
  onSelect: (id: string | null) => void
  onSearchChange: (value: string) => void
}

export function EditCaseClientDropdown({
  clients,
  selectedClientId,
  showDropdown,
  clientSearch,
  onToggle,
  onSelect,
  onSearchChange,
}: EditCaseClientDropdownProps): ReactElement {
  const { t } = useTranslation(['cases'])
  const selectedClient = clients.find((client) => client.id === selectedClientId)
  return (
    <div className="relative">
      <label
        htmlFor="edit-client"
        className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {t('newCase.clientProfile')}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={onToggle}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 pr-10 text-left text-slate-900 transition-all focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        >
          {selectedClient ? (
            <span>
              {selectedClient.name}
              {selectedClient.shortName && (
                <span className="ml-2 text-slate-500 dark:text-slate-400">
                  ({selectedClient.shortName})
                </span>
              )}
            </span>
          ) : (
            <span className="text-slate-400 dark:text-slate-500">
              {t('editCase.noClientAssigned')}
            </span>
          )}
        </button>
        <ChevronDown className="absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
      {showDropdown && (
        <ClientDropdownPanel
          clients={clients}
          selectedClientId={selectedClientId}
          clientSearch={clientSearch}
          onSelect={onSelect}
          onSearchChange={onSearchChange}
        />
      )}
    </div>
  )
}

export function EditCasePriorityGrid({
  priority,
  onSelect,
}: {
  priority: CasePriority
  onSelect: (p: CasePriority) => void
}): ReactElement {
  const { t } = useTranslation(['cases'])
  const priorities: { value: CasePriority; label: string; description: string }[] = [
    { value: 'critical', label: t('priority.critical'), description: t('priority.criticalDesc') },
    { value: 'high', label: t('priority.high'), description: t('priority.highDesc') },
    { value: 'medium', label: t('priority.medium'), description: t('priority.mediumDesc') },
    { value: 'low', label: t('priority.low'), description: t('priority.lowDesc') },
  ]
  return (
    <div>
      <label className="mb-3 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('newCase.priorityLevel')}
      </label>
      <div className="grid grid-cols-2 gap-3">
        {priorities.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              onSelect(opt.value)
            }}
            className={`relative flex items-start gap-3 rounded-lg border-2 p-3 transition-all ${priority === opt.value ? 'border-[#4e5e82] bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10' : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'}`}
          >
            <span
              className={`mt-0.5 h-3 w-3 flex-shrink-0 rounded-full ${priorityColors[opt.value]}`}
            />
            <div className="text-left">
              <span
                className={`block text-sm font-medium ${priority === opt.value ? 'text-lime-700 dark:text-lime-300' : 'text-slate-900 dark:text-slate-100'}`}
              >
                {opt.label}
              </span>
              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                {opt.description}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function EditCaseNameField({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}): ReactElement {
  const { t } = useTranslation(['cases'])
  return (
    <div>
      <label
        htmlFor="edit-case-name"
        className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {t('newCase.caseName')} <span className="text-red-500">*</span>
      </label>
      <input
        id="edit-case-name"
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value) }}
        placeholder={t('newCase.caseNamePlaceholder')}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-900 transition-all placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
      />
    </div>
  )
}

function EditCaseTextFields({
  form,
}: {
  form: EditCaseFormState
}): ReactElement {
  const { t } = useTranslation(['cases'])
  return (
    <>
      <div>
        <label
          htmlFor="edit-org-ref"
          className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {t('newCase.orgReference')}
        </label>
        <input
          id="edit-org-ref"
          type="text"
          value={form.orgCaseReference}
          onChange={(e) => { form.setOrgCaseReference(e.target.value) }}
          placeholder={t('newCase.orgReferencePlaceholder')}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 font-mono text-sm text-slate-900 transition-all placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
      </div>
      <div>
        <label
          htmlFor="edit-scope"
          className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {t('newCase.scopeDescription')} <span className="text-red-500">*</span>
        </label>
        <textarea
          id="edit-scope"
          value={form.scopeDescription}
          onChange={(e) => { form.setScopeDescription(e.target.value) }}
          placeholder={t('newCase.scopePlaceholder')}
          rows={4}
          className="w-full resize-none rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-900 transition-all placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
      </div>
    </>
  )
}

function EditCaseFormActions({
  isValid,
  isUpdating,
  onClose,
}: {
  isValid: boolean
  isUpdating: boolean
  onClose: (() => void) | undefined
}): ReactElement {
  const { t } = useTranslation(['cases', 'common'])
  return (
    <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-4 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={!isValid || isUpdating}
        className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-6 py-2.5 font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-700"
      >
        {isUpdating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('editCase.saving')}
          </>
        ) : (
          <>
            <Save className="h-4 w-4" />
            {t('editCase.saveChanges')}
          </>
        )}
      </button>
    </div>
  )
}

interface EditCaseFormProps {
  form: EditCaseFormState
  clients: ClientProfile[]
  caseData: Case
  isUpdating: boolean
  onClose: (() => void) | undefined
  onClientSelect: (id: string | null) => void
}

export function EditCaseForm({
  form,
  clients,
  isUpdating,
  onClose,
  onClientSelect,
}: EditCaseFormProps): ReactElement {
  return (
    <form onSubmit={form.handleSubmit} className="space-y-5 p-6">
      <EditCaseNameField value={form.name} onChange={form.setName} />
      <EditCaseClientDropdown
        clients={clients}
        selectedClientId={form.selectedClientId}
        showDropdown={form.showClientDropdown}
        clientSearch={form.clientSearch}
        onToggle={() => { form.setShowClientDropdown(!form.showClientDropdown) }}
        onSelect={onClientSelect}
        onSearchChange={form.setClientSearch}
      />
      <EditCaseTextFields form={form} />
      <EditCasePriorityGrid priority={form.priority} onSelect={form.setPriority} />
      <EditCaseFormActions isValid={form.isValid} isUpdating={isUpdating} onClose={onClose} />
    </form>
  )
}
